import { InvoiceHire } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import type { StatCard } from '../../components/Common';

export interface InvoiceHireListFilters {
  searchTerm: string;
  statusFilter: string;
  typeFilter: string;
  /** All ids (documentId + numeric id) of the selected client, or null for "all" */
  clientIds: Set<string> | null;
  currencyFilter: string;
  dateFromFilter: string;
  dateToFilter: string;
  amountFromFilter: string;
  amountToFilter: string;
}

/**
 * Total deducted per invoice (in the invoice's own currency), keyed by every
 * id the invoice may be referenced with (documentId and numeric id).
 */
export function buildDeductionTotals(deductions: any[]): Map<string, number> {
  const totals = new Map<string, number>();
  (deductions || []).forEach((d: any) => {
    if (d.status === 'cancelled' || !d.invoiceId) return;
    const applied = Number(d.invoiceAppliedAmount ?? d.amount) || 0;
    if (!applied) return;
    totals.set(String(d.invoiceId), (totals.get(String(d.invoiceId)) || 0) + applied);
  });
  return totals;
}

export function deductionTotalFor(totals: Map<string, number>, invoice: InvoiceHire): number {
  return totals.get(String(invoice.documentId)) ?? totals.get(String(invoice.id)) ?? 0;
}

/** All page filters (the paid-invoices exclusion is applied separately for the table) */
export function matchesHireFilters(
  invoice: InvoiceHire,
  f: InvoiceHireListFilters,
  getClientName: (clientId: string) => string
): boolean {
  const clientName = getClientName(invoice.customerId);
  const matchesSearch = !f.searchTerm || (
    invoice.invoiceNumber.toLowerCase().includes(f.searchTerm.toLowerCase()) ||
    clientName.toLowerCase().includes(f.searchTerm.toLowerCase()) ||
    invoice.type.toLowerCase().includes(f.searchTerm.toLowerCase()) ||
    invoice.status.toLowerCase().includes(f.searchTerm.toLowerCase()) ||
    Boolean(invoice.projectId && invoice.projectId.toLowerCase().includes(f.searchTerm.toLowerCase()))
  );
  const matchesStatus = f.statusFilter === 'all' || invoice.status === f.statusFilter;
  const matchesType = f.typeFilter === 'all' || invoice.type === f.typeFilter;
  const matchesClient = !f.clientIds || f.clientIds.has(String(invoice.customerId));
  const invoiceDate = new Date(invoice.invoiceDate);
  const matchesDateFrom = !f.dateFromFilter || invoiceDate >= new Date(f.dateFromFilter);
  const matchesDateTo = !f.dateToFilter || invoiceDate <= new Date(f.dateToFilter);
  const matchesAmountFrom = !f.amountFromFilter || invoice.netTotal >= parseFloat(f.amountFromFilter);
  const matchesAmountTo = !f.amountToFilter || invoice.netTotal <= parseFloat(f.amountToFilter);
  const matchesCurrency = f.currencyFilter === 'all' || resolveCurrencyCode(invoice.currency) === f.currencyFilter;
  return matchesSearch && matchesStatus && matchesType && matchesClient &&
    matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo && matchesCurrency;
}

/** Rows for the Excel export/print of the invoice list */
export function buildHireExportRows(
  hires: InvoiceHire[],
  getClientName: (clientId: string) => string,
  deductionTotals: Map<string, number>
): Record<string, any>[] {
  return hires.map((invoice) => ({
    'Invoice ID': invoice.id,
    'Customer': getClientName(invoice.customerId),
    'Invoice Date': new Date(invoice.invoiceDate).toLocaleDateString(),
    'Original Total': invoice.originalTotal ?? '-',
    'Deductions': deductionTotalFor(deductionTotals, invoice) || '-',
    'Remaining (Net)': invoice.netTotal,
    'Status': invoice.status,
  }));
}

const fmt = (n: number) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** e.g. "Total: 22,000 USD + 5,000 EGP" — never a hardcoded $ */
function totalsByCurrency(hires: InvoiceHire[]): string {
  const totals: Record<string, number> = {};
  hires.forEach((i) => {
    const code = resolveCurrencyCode(i.currency) || 'EGP';
    totals[code] = (totals[code] || 0) + (Number(i.originalTotal ?? i.netTotal) || 0);
  });
  const parts = Object.entries(totals).map(([code, v]) => `${fmt(v)} ${code}`);
  return `Total: ${parts.length > 0 ? parts.join(' + ') : '0'}`;
}

export function buildStatusStats(hires: InvoiceHire[]): StatCard[] {
  return (['draft', 'sent', 'paid', 'overdue', 'cancelled'] as const).map((status) => ({
    label: status.charAt(0).toUpperCase() + status.slice(1),
    value: hires.filter((i) => i.status === status).length,
    color: ({ draft: 'gray', sent: 'blue', paid: 'green', overdue: 'red', cancelled: 'yellow' } as const)[status],
  }));
}

export const typeLabels: Record<string, string> = {
  invoice_hire: 'Hire', invoice_accommodation: 'Accommodation',
  debit_note: 'Debit', credit_note: 'Credit',
};
const typeColorMap: Record<string, 'purple' | 'yellow' | 'red' | 'green'> = {
  invoice_hire: 'purple', invoice_accommodation: 'yellow', debit_note: 'red', credit_note: 'green',
};

export function buildTypeStats(hires: InvoiceHire[]): StatCard[] {
  return (['invoice_hire', 'invoice_accommodation', 'debit_note', 'credit_note'] as const).map((type) => {
    const ofType = hires.filter((i) => i.type === type);
    return {
      label: `${typeLabels[type]} Invoices`,
      value: ofType.length,
      subtitle: totalsByCurrency(ofType),
      color: typeColorMap[type],
    };
  });
}
